/**
 * Turning-point wave graph — a self-contained SVG bell curve.
 *
 * The pinnacle (top of the wave) is the nearest turning-point date. The
 * flanks — where the Turning Point period begins — sit low and are drawn
 * in cyan, rising through green → yellow → orange to red at the crest.
 * A pulsing blue dot marks where "today" falls on the wave; it appears
 * only inside the ±window (default ±22 days) and fades out beyond it.
 * Small dots every 7 days mark each 7-day stretch of the window on the
 * curve itself, tinted with the curve colour at that height.
 *
 * Pure string builder: no DOM, no dependencies.
 */

const WIDTH = 640;
const HEIGHT = 210;
const PAD_X = 26;
const PAD_TOP = 30;
const PAD_BOTTOM = 44;

/** Days between the small interval dots drawn along the wave. */
const INTERVAL_DAYS = 7;

/** Stroke-gradient stops (lowest → highest) so the interval dots match the curve. */
const WAVE_STOPS = [
  { at: 0, rgb: [34, 211, 238] },    // #22d3ee
  { at: 0.3, rgb: [52, 211, 153] },  // #34d399
  { at: 0.55, rgb: [250, 204, 21] }, // #facc15
  { at: 0.78, rgb: [251, 146, 60] }, // #fb923c
  { at: 1, rgb: [239, 68, 68] },     // #ef4444
];

/** Colour of the wave stroke at a 0 (flank) … 1 (crest) height ratio. */
function waveColorAtRatio(ratio) {
  const t = Math.min(1, Math.max(0, ratio));
  for (let i = 1; i < WAVE_STOPS.length; i++) {
    const lo = WAVE_STOPS[i - 1];
    const hi = WAVE_STOPS[i];
    if (t <= hi.at) {
      const mix = (t - lo.at) / (hi.at - lo.at || 1);
      const channel = (index) =>
        Math.round(lo.rgb[index] + (hi.rgb[index] - lo.rgb[index]) * mix);
      return `rgb(${channel(0)}, ${channel(1)}, ${channel(2)})`;
    }
  }
  return "rgb(239, 68, 68)";
}

function esc(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** MM/DD for the small 7-day labels (the crest keeps the full date). */
function shortDate(date) {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${m}/${day}`;
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

/**
 * @param {number|null} diffDays  Days from reference to the turning point the wave centres on.
 * @param {object} [opts]
 * @param {number} [opts.windowDays=22]  Half-window shown each side of the pinnacle.
 * @param {string} [opts.peakLabel]      Label under the pinnacle (e.g. the TP date).
 * @param {string} [opts.dotLabel]       Label under the blue dot (e.g. "Today").
 * @param {Date|string} [opts.centerDate]  Date of the turning point at the crest, so
 *                                         every 7-day marker can be given its own date.
 * @returns {string} SVG markup.
 */
export function buildWaveGraph(diffDays, opts = {}) {
  const windowDays = opts.windowDays ?? 22;
  const peakLabel = opts.peakLabel || "Turning point";
  const dotLabel = opts.dotLabel || "Today";
  const centerDate = opts.centerDate ? new Date(opts.centerDate) : null;
  const hasCenterDate = Boolean(centerDate && !Number.isNaN(centerDate.getTime()));

  const innerW = WIDTH - PAD_X * 2;
  const baseY = HEIGHT - PAD_BOTTOM;
  const peakY = PAD_TOP;
  const amp = baseY - peakY;
  const sigma = windowDays * 0.5;

  const xFromDay = (d) => PAD_X + ((d + windowDays) / (2 * windowDays)) * innerW;
  const yFromDay = (d) => baseY - amp * Math.exp(-((d / sigma) ** 2));
  /** Curve colour at a given day, so the interval dots match the wave there. */
  const colorFromDay = (d) => waveColorAtRatio((baseY - yFromDay(d)) / amp);

  // Small dots every 7 days either side of the crest, so each 7-day stretch of
  // the window is visible on the curve. Day 0 carries the pinnacle dot already.
  // Each marker is labelled with its date, set in smaller type than the crest
  // date and tucked against the curve just above its dot.
  const maxMarkDays = Math.floor(windowDays / INTERVAL_DAYS) * INTERVAL_DAYS;
  const intervalDots = [];
  for (let d = -maxMarkDays; d <= maxMarkDays; d += INTERVAL_DAYS) {
    if (d === 0) continue;
    const cx = xFromDay(d);
    const cy = yFromDay(d);
    const label = hasCenterDate ? shortDate(addDays(centerDate, d)) : "";
    intervalDots.push(
      `  <circle class="ga-wave-interval-dot" cx="${cx.toFixed(2)}" cy="${cy.toFixed(2)}" r="3.6" fill="${colorFromDay(d)}" />` +
        (label
          ? `\n  <text class="ga-wave-interval-label" x="${cx.toFixed(2)}" y="${(cy - 9).toFixed(2)}" text-anchor="middle">${esc(label)}</text>`
          : "")
    );
  }

  // Smooth bell path sampled across the window.
  const samples = 140;
  let path = "";
  for (let i = 0; i <= samples; i++) {
    const d = -windowDays + (2 * windowDays) * (i / samples);
    const x = xFromDay(d).toFixed(2);
    const y = yFromDay(d).toFixed(2);
    path += (i === 0 ? "M" : "L") + x + " " + y + " ";
  }

  const inWindow =
    typeof diffDays === "number" &&
    Math.abs(diffDays) <= windowDays;
  const clamped = inWindow ? diffDays : 0;
  const dotX = xFromDay(clamped).toFixed(2);
  const dotY = yFromDay(clamped).toFixed(2);

  const fillPath = `${path} L ${xFromDay(windowDays).toFixed(2)} ${baseY} L ${xFromDay(-windowDays).toFixed(2)} ${baseY} Z`;

  const dot = inWindow
    ? `
        <line class="ga-wave-drop" x1="${dotX}" y1="${dotY}" x2="${dotX}" y2="${baseY}" />
        <circle class="ga-wave-dot-halo" cx="${dotX}" cy="${dotY}" r="9" />
        <circle class="ga-wave-dot" cx="${dotX}" cy="${dotY}" r="5.5" />
        <text class="ga-wave-dot-label" x="${dotX}" y="${HEIGHT - 8}" text-anchor="middle">${esc(dotLabel)}</text>`
    : "";

  return `
<svg class="ga-wave-svg" viewBox="0 0 ${WIDTH} ${HEIGHT}" role="img"
     aria-label="Turning-point wave: the crest marks the turning point, with a dot every ${INTERVAL_DAYS} days${inWindow ? " and today marked on the slope" : ""}."
     preserveAspectRatio="xMidYMid meet">
  <defs>
    <linearGradient id="gaWaveStroke" x1="0" y1="1" x2="0" y2="0">
      <stop offset="0%" stop-color="#22d3ee" />
      <stop offset="30%" stop-color="#34d399" />
      <stop offset="55%" stop-color="#facc15" />
      <stop offset="78%" stop-color="#fb923c" />
      <stop offset="100%" stop-color="#ef4444" />
    </linearGradient>
    <linearGradient id="gaWaveFill" x1="0" y1="1" x2="0" y2="0">
      <stop offset="0%" stop-color="#22d3ee" stop-opacity="0.05" />
      <stop offset="55%" stop-color="#facc15" stop-opacity="0.10" />
      <stop offset="100%" stop-color="#ef4444" stop-opacity="0.22" />
    </linearGradient>
  </defs>

  <!-- baseline + window ticks -->
  <line class="ga-wave-axis" x1="${PAD_X}" y1="${baseY}" x2="${WIDTH - PAD_X}" y2="${baseY}" />
  <line class="ga-wave-tick" x1="${xFromDay(-windowDays).toFixed(2)}" y1="${baseY}" x2="${xFromDay(-windowDays).toFixed(2)}" y2="${baseY + 6}" />
  <line class="ga-wave-tick" x1="${xFromDay(windowDays).toFixed(2)}" y1="${baseY}" x2="${xFromDay(windowDays).toFixed(2)}" y2="${baseY + 6}" />
  <line class="ga-wave-tick ga-wave-tick--center" x1="${xFromDay(0).toFixed(2)}" y1="${baseY}" x2="${xFromDay(0).toFixed(2)}" y2="${peakY}" />

  <path class="ga-wave-fill" d="${fillPath}" fill="url(#gaWaveFill)" />
  <path class="ga-wave-curve" d="${path.trim()}" fill="none" stroke="url(#gaWaveStroke)" />

${intervalDots.join("\n")}
  <circle class="ga-wave-pinnacle" cx="${xFromDay(0).toFixed(2)}" cy="${peakY}" r="4.5" />
  <text class="ga-wave-peak-label" x="${xFromDay(0).toFixed(2)}" y="${peakY - 12}" text-anchor="middle">${esc(peakLabel)}</text>
  <text class="ga-wave-edge-label" x="${PAD_X}" y="${HEIGHT - 8}" text-anchor="start">${windowDays} days before</text>
  <text class="ga-wave-edge-label" x="${WIDTH - PAD_X}" y="${HEIGHT - 8}" text-anchor="end">${windowDays} days after</text>
  ${dot}
</svg>`.trim();
}
